/**
 * Revenue Chart Component
 * Displays revenue trends over time
 */

import React from "react";
import {
	CartesianGrid,
	Legend,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";

interface RevenueChartProps {
	data: Array<{
		month: string;
		revenue: number;
		pending: number;
	}>;
}

const RevenueChart: React.FC<RevenueChartProps> = ({ data }) => {
	const formatCurrency = (value: number) => {
		return new Intl.NumberFormat("en-NG", {
			style: "currency",
			currency: "NGN",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(value);
	};

	return (
		<div className="bg-card border border-border rounded-lg p-6">
			<h3 className="text-lg font-semibold text-foreground mb-4">
				Revenue Trends
			</h3>
			<ResponsiveContainer width="100%" height={300}>
				<LineChart data={data}>
					<CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
					<XAxis
						dataKey="month"
						stroke="hsl(var(--muted-foreground))"
						style={{ fontSize: "12px" }}
					/>
					<YAxis
						stroke="hsl(var(--muted-foreground))"
						style={{ fontSize: "12px" }}
						tickFormatter={(value) => {
							if (value >= 1000000) return `₦${(value / 1000000).toFixed(1)}M`;
							if (value >= 1000) return `₦${(value / 1000).toFixed(0)}K`;
							return `₦${value}`;
						}}
					/>
					<Tooltip
						contentStyle={{
							backgroundColor: "hsl(var(--card))",
							border: "1px solid hsl(var(--border))",
							borderRadius: "6px",
						}}
						formatter={(value: number) => formatCurrency(value)}
					/>
					<Legend />
					<Line
						type="monotone"
						dataKey="revenue"
						stroke="hsl(var(--success))"
						strokeWidth={2}
						name="Revenue"
						dot={{ fill: "hsl(var(--success))", r: 4 }}
					/>
					<Line
						type="monotone"
						dataKey="pending"
						stroke="hsl(var(--warning))"
						strokeWidth={2}
						name="Pending"
						dot={{ fill: "hsl(var(--warning))", r: 4 }}
					/>
				</LineChart>
			</ResponsiveContainer>
		</div>
	);
};

export default RevenueChart;
